import round from 'lodash/round';
import Papa from 'papaparse';
import dayjs from '~/timeInit';

import {
  DailyRow,
  DateRange,
  LE_COLUMN_PATTERNS,
  MIN_DATA_COVERAGE,
  OTA_COLORS,
  OTA_CSV_ALIASES,
  OtaChartData,
  ParityAnalysis,
  SeverityLevel,
  TRACKED_OTAS,
} from './types';

interface RangeAccumulator {
  start: string;
  end: string;
  severity: SeverityLevel;
  comp: string;
  prices: number[];
  compPrices: number[];
  diffs: number[];
}

function emptyAnalysis(propertyName: string): ParityAnalysis {
  return {
    propertyName,
    analysisDate: new Date().toISOString().slice(0, 10),
    dateRangeStart: '',
    dateRangeEnd: '',
    totalDates: 0,
    totalCalendarDays: 0,
    inParityCount: 0,
    outOfParityCount: 0,
    parityScore: 0,
    parityRating: 'CRITICAL',
    avgPriceDiff: 0,
    avgPriceDiffPct: 0,
    worstCompetitor: 'N/A',
    competitorCheapestCounts: {},
    severityCounts: {},
    dateRanges: [],
    dailyDetail: [],
    otaCharts: [],
    leColumn: '',
    numCompetitors: 0,
    yMin: 0,
    yMax: 1000,
  };
}

export function analyzeParityCsv(csvText: string, propertyName: string): ParityAnalysis {
  const parsed = Papa.parse<Record<string, string>>(csvText, { header: true, dynamicTyping: false });
  const rawRows = parsed.data.filter((r) => Object.values(r).some((v) => v !== '' && v != null));

  if (rawRows.length === 0) return emptyAnalysis(propertyName);

  const columns = Object.keys(rawRows[0]);
  const dateCol = findDateColumn(columns);
  const leCol = findLeColumn(columns, dateCol);
  const competitorCols = matchTrackedOtas(columns, dateCol, leCol);

  const dateSet = new Set<string>();
  for (const row of rawRows) {
    const d = row[dateCol];
    if (d) dateSet.add(d);
  }

  if (dateSet.size === 0) return emptyAnalysis(propertyName);

  const sortedDates = Array.from(dateSet).sort();
  const allDates = generateDateRange(sortedDates[0], sortedDates[sortedDates.length - 1]);

  const rowByDate = new Map<string, Record<string, string>>();
  for (const row of rawRows) {
    const d = row[dateCol];
    if (d) rowByDate.set(d, row);
  }

  const dailyDetail: DailyRow[] = [];
  const allValues: number[] = [];

  for (const date of allDates) {
    const row = rowByDate.get(date);
    if (!row) {
      dailyDetail.push({
        date,
        lePrice: null,
        bestCompetitor: null,
        bestCompetitorPrice: null,
        priceDiff: null,
        priceDiffPct: null,
        severity: 'no_data',
        allPrices: {},
      });
      continue;
    }

    const lePrice = parseNum(row[leCol]);
    const allPrices: Record<string, number> = {};
    let bestComp: string | null = null;
    let bestPrice = Infinity;

    for (const [otaName, csvCol] of Object.entries(competitorCols)) {
      const p = parseNum(row[csvCol]);
      if (p !== null && p > 0) {
        allPrices[otaName] = p;
        if (p < bestPrice) {
          bestPrice = p;
          bestComp = otaName;
        }
      }
    }

    if (lePrice !== null) allValues.push(lePrice);
    Object.values(allPrices).forEach((v) => allValues.push(v));

    if (lePrice === null && Object.keys(allPrices).length === 0) {
      dailyDetail.push({
        date,
        lePrice: null,
        bestCompetitor: null,
        bestCompetitorPrice: null,
        priceDiff: null,
        priceDiffPct: null,
        severity: 'no_data',
        allPrices: {},
      });
      continue;
    }

    let severity: SeverityLevel;
    let diff: number | null = null;
    let diffPct: number | null = null;

    if (lePrice !== null && bestComp !== null) {
      diff = round(lePrice - bestPrice);
      diffPct = lePrice > 0 ? round(((lePrice - bestPrice) / lePrice) * 100, 1) : 0;
      severity = classifySeverity(diffPct / 100);
    } else {
      severity = 'no_comparison';
    }

    dailyDetail.push({
      date,
      lePrice: lePrice !== null ? round(lePrice) : null,
      bestCompetitor: bestComp,
      bestCompetitorPrice: bestComp ? round(bestPrice) : null,
      priceDiff: diff,
      priceDiffPct: diffPct,
      severity,
      allPrices,
    });
  }

  const comparable = dailyDetail.filter((d) => !['no_data', 'no_comparison'].includes(d.severity));
  const inParity = comparable.filter((d) => d.severity === 'in_parity').length;
  const totalComparable = comparable.length;
  const parityScore = totalComparable > 0 ? Math.round((inParity / totalComparable) * 100) : 0;

  const outOfParity = comparable.filter((d) => d.severity !== 'in_parity');
  const competitorCheapestCounts: Record<string, number> = {};
  for (const d of outOfParity) {
    if (d.bestCompetitor) {
      competitorCheapestCounts[d.bestCompetitor] = (competitorCheapestCounts[d.bestCompetitor] ?? 0) + 1;
    }
  }

  const withDiff = comparable.filter((d) => d.priceDiff !== null && d.priceDiff > 0);
  const avgDiff = withDiff.length > 0 ? round(withDiff.reduce((s, d) => s + d.priceDiff!, 0) / withDiff.length) : 0;
  const avgDiffPct =
    withDiff.length > 0 ? round(withDiff.reduce((s, d) => s + d.priceDiffPct!, 0) / withDiff.length, 1) : 0;

  const worstCompetitor = Object.entries(competitorCheapestCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'N/A';

  const severityCounts: Record<string, number> = {};
  for (const d of dailyDetail) {
    severityCounts[d.severity] = (severityCounts[d.severity] ?? 0) + 1;
  }

  const dateRanges = groupIntoDateRanges(dailyDetail);
  const otaCharts = buildOtaCharts(dailyDetail, competitorCols, competitorCheapestCounts);

  const padding = allValues.length > 0 ? (Math.max(...allValues) - Math.min(...allValues)) * 0.05 : 100;
  const yMin = allValues.length > 0 ? Math.max(0, Math.floor(Math.min(...allValues) - padding)) : 0;
  const yMax = allValues.length > 0 ? Math.ceil(Math.max(...allValues) + padding) : 1000;

  return {
    propertyName,
    analysisDate: new Date().toISOString().slice(0, 10),
    dateRangeStart: allDates[0],
    dateRangeEnd: allDates[allDates.length - 1],
    totalDates: totalComparable,
    totalCalendarDays: allDates.length,
    inParityCount: inParity,
    outOfParityCount: totalComparable - inParity,
    parityScore,
    parityRating: parityScore >= 80 ? 'GOOD' : parityScore >= 60 ? 'FAIR' : parityScore >= 40 ? 'POOR' : 'CRITICAL',
    avgPriceDiff: avgDiff,
    avgPriceDiffPct: avgDiffPct,
    worstCompetitor,
    competitorCheapestCounts,
    severityCounts,
    dateRanges,
    dailyDetail,
    otaCharts,
    leColumn: leCol,
    numCompetitors: Object.keys(competitorCols).length,
    yMin,
    yMax,
  };
}

function findDateColumn(columns: string[]): string {
  const dateCols = columns.filter((c) => c.toLowerCase().includes('date'));
  const nonReport = dateCols.filter((c) => !c.toLowerCase().includes('report'));
  return nonReport[0] ?? dateCols[0] ?? columns[0];
}

function findLeColumn(columns: string[], dateCol: string): string {
  const nonDate = columns.filter((c) => c !== dateCol);
  for (const col of nonDate) {
    const lower = col.toLowerCase();
    if (LE_COLUMN_PATTERNS.some((p) => lower.includes(p))) return col;
  }
  return nonDate[0];
}

function matchTrackedOtas(columns: string[], dateCol: string, leCol: string): Record<string, string> {
  const available = columns.filter((c) => c !== dateCol && c !== leCol);
  const matched: Record<string, string> = {};
  for (const ota of TRACKED_OTAS) {
    const aliases = OTA_CSV_ALIASES[ota];
    for (const col of available) {
      const lower = col.toLowerCase();
      if (aliases.some((a) => lower.includes(a))) {
        matched[ota] = col;
        break;
      }
    }
  }
  return matched;
}

function generateDateRange(start: string, end: string): string[] {
  const dates: string[] = [];
  let current = dayjs(start);
  const last = dayjs(end);
  while (!current.isAfter(last, 'day')) {
    dates.push(current.format('YYYY-MM-DD'));
    current = current.add(1, 'day');
  }
  return dates;
}

function classifySeverity(diffPct: number): SeverityLevel {
  if (diffPct <= 0.02) return 'in_parity';
  if (diffPct <= 0.05) return 'slight';
  if (diffPct <= 0.1) return 'moderate';
  return 'severe';
}

function groupIntoDateRanges(daily: DailyRow[]): DateRange[] {
  const oop = daily.filter(
    (
      d,
    ): d is DailyRow & { bestCompetitor: string; lePrice: number; bestCompetitorPrice: number; priceDiffPct: number } =>
      !['in_parity', 'no_data', 'no_comparison'].includes(d.severity) &&
      d.bestCompetitor !== null &&
      d.lePrice !== null &&
      d.bestCompetitorPrice !== null &&
      d.priceDiffPct !== null,
  );
  if (oop.length === 0) return [];

  const ranges: DateRange[] = [];
  let cur: RangeAccumulator | null = null;

  for (const d of oop) {
    const daysBetween = cur ? dayjs(d.date).diff(dayjs(cur.end), 'day') : 0;

    if (!cur || d.severity !== cur.severity || d.bestCompetitor !== cur.comp || daysBetween > 2) {
      if (cur) ranges.push(finalizeRange(cur));
      cur = {
        start: d.date,
        end: d.date,
        severity: d.severity,
        comp: d.bestCompetitor,
        prices: [d.lePrice],
        compPrices: [d.bestCompetitorPrice],
        diffs: [d.priceDiffPct],
      };
    } else {
      cur.end = d.date;
      cur.prices.push(d.lePrice);
      cur.compPrices.push(d.bestCompetitorPrice);
      cur.diffs.push(d.priceDiffPct);
    }
  }
  if (cur) ranges.push(finalizeRange(cur));

  const order: Record<string, number> = { severe: 0, moderate: 1, slight: 2 };
  return ranges.sort((a, b) => (order[a.severity] ?? 3) - (order[b.severity] ?? 3) || b.avgDiffPct - a.avgDiffPct);
}

function finalizeRange(r: RangeAccumulator): DateRange {
  const avg = (arr: number[]) => arr.reduce((s, v) => s + v, 0) / arr.length;
  return {
    startDate: r.start,
    endDate: r.end,
    numDays: dayjs(r.end).diff(dayjs(r.start), 'day') + 1,
    avgLePrice: round(avg(r.prices)),
    avgCompetitorPrice: round(avg(r.compPrices)),
    avgDiffPct: round(avg(r.diffs), 1),
    competitor: r.comp,
    severity: r.severity,
  };
}

function buildOtaCharts(
  daily: DailyRow[],
  competitorCols: Record<string, string>,
  counts: Record<string, number>,
): OtaChartData[] {
  const total = daily.length;
  const charts: OtaChartData[] = [];

  for (const ota of TRACKED_OTAS) {
    if (!(ota in competitorCols)) continue;
    const data = daily.map((d) => ({
      date: d.date,
      lePrice: d.lePrice,
      otaPrice: d.allPrices[ota] ?? null,
    }));
    const available = data.filter((d) => d.otaPrice !== null).length;
    if (total > 0 && available / total < MIN_DATA_COVERAGE) continue;

    charts.push({
      name: ota,
      color: OTA_COLORS[ota],
      cheapestCount: counts[ota] ?? 0,
      availableCount: available,
      totalDates: total,
      data,
    });
  }

  return charts;
}

function parseNum(val: string | undefined | null): number | null {
  if (val == null || val === '') return null;
  const n = Number(val);
  return isNaN(n) || n <= 0 ? null : n;
}
